import Grid from '@mui/material/Grid2';
import { useEffect, useMemo, useState } from 'react';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import { Box, Card, Typography } from '@mui/material';
import { baseChartSpacing, ChartComponent } from 'src/view/components/chartComponent';
import { SiteCard } from 'src/view/components/ProjectDetailComponent';
import SiteArea from 'src/resources/images/area_site.png';
import SitePlantation from 'src/resources/images/planted_area_site (1).png';
import SiteTree from 'src/resources/images/planted_tree_site.png';
import { CreditCard } from 'src/view/components/cardComponent';
import { useAppState } from 'src/state-management/state-provider';
import { CarbonSiteAnalysis, Site, UserRole } from 'src/api/Api';
import SiteMapWithLayer from 'src/view/DMap/SiteMapWithLayer';
import { useParams } from 'react-router-dom';
import { roundToTwoDecimalPlaces } from 'src/util/util';
import { CustomRasterLayer } from 'src/mapbox-helpers/map-types';
import { CARBON_GLOBAL_LAYERS } from 'src/mapbox-helpers/global-datasets';
import { SampleProjectId } from 'src/enum';

const CarbonAnalysisDetails = () => {
  const { state } = useAppState();
  const { selectedProject, selectedSite, user } = state;
  const { carbonSiteAnalysis, projectDetails } = selectedProject;
  const [categories, setCategories] = useState<string[]>([]);
  const [seriesData, setSeriesData] = useState<{ name: string; data: number[] }[]>([]);
  const [carbonAnalysisData, setCarbonAnalysisData] = useState<CarbonSiteAnalysis | undefined>();
  const [site, setSite] = useState<Site | undefined>();
  const { siteId } = useParams();
  useEffect(() => {
    setTimeout(() => {
      // @ts-ignore
      const carbonAnalysisData: CarbonSiteAnalysis | undefined = carbonSiteAnalysis?.find(
        (i: CarbonSiteAnalysis) => i.site_id === selectedSite?.site_id,
      );

      const site: Site | undefined = projectDetails?.sites?.find(
        (site: Site) => site.site_id === selectedSite?.site_id,
      );
      setSite(site || undefined);
      setCarbonAnalysisData(carbonAnalysisData || undefined);

      if (!carbonAnalysisData) return;

      const filterTimeSeriesData = carbonAnalysisData ? carbonAnalysisData?.lulc_timeseries : '';

      if (!filterTimeSeriesData) return;
      const categories = Object.keys(filterTimeSeriesData).filter((key) => !isNaN(Number(key)));
      setCategories(categories);
      const firstYear = categories[0];
      if (!firstYear) return;

      const seriesNames = Object.keys(filterTimeSeriesData[firstYear] || {});
      const seriesData = seriesNames.map((name) => ({
        name,
        data: categories.map((year) => filterTimeSeriesData[year]?.[name] || 0),
      }));

      setSeriesData(seriesData);

      // scroll to top
      window.scrollTo({
        top: 0,
        behavior: 'smooth', // Smooth scrolling
      });
    }, 300);
  }, [siteId, carbonSiteAnalysis]);

  const filterTransitionData = carbonAnalysisData
    ? carbonAnalysisData?.lulc_transition_piechart
    : '';
  // @ts-ignore
  const filterWaterData = carbonAnalysisData?.surface_water_transition || {};
  // @ts-ignore
  const filterSoilData = carbonAnalysisData?.soc || {};
  // @ts-ignore
  const filterSoilPropertyData: any = carbonAnalysisData ? carbonAnalysisData?.soil_properties : '';

  const soilPropertyDataCategories = useMemo(() => {
    if (!filterSoilPropertyData || !filterSoilPropertyData['sand']) return [];
    return Object.keys(filterSoilPropertyData['sand']).filter((key) =>
      key.endsWith('cm'),
    );
  }, [filterSoilPropertyData]);

  // Create series data for each soc object
  const series = useMemo(() => {
    if (!filterSoilPropertyData) return [];
    return Object.entries(filterSoilPropertyData).map(([key, data]) => ({
      name: key,
      data: soilPropertyDataCategories.map((category) => (data as Record<string, number>)[category]),
    }));
  }, [filterSoilPropertyData, soilPropertyDataCategories]);

  const soilDataCategories = useMemo(() => {
    if (!filterSoilData) return [];
    return Object.keys(filterSoilData).filter((key) => key.endsWith('cm'));
  }, [filterSoilData]);

  const series2 = useMemo(() => {
    if (!filterSoilData) return [];
    return [
      {
        name: '', // Series name
        data: soilDataCategories.map((category) => filterSoilData[category] || 0), // Map depth levels to their values
        color: '#45C8FF', // Default color for the series
      },
    ];
  }, [soilDataCategories, filterSoilData]);
      const isUserRole = useMemo(() => {
        if (!user?.role || user.role.length === 0) return false;
        // Show only if user has ONLY User role (no Admin or Employee)
        const roles = user.role;
        const hasUserRole = roles.includes(UserRole.User);
        const hasAdminRole = roles.includes(UserRole.Admin);
        const hasEmployeeRole = roles.includes(UserRole.Employee);
        // Only show if user has User role and doesn't have Admin or Employee
        return hasUserRole && !hasAdminRole && !hasEmployeeRole;
      }, [user]);
      const isSampleProject = projectDetails?.project_id === SampleProjectId;
      // Disable exporting when project is the sample project AND the current user
      // has only the "User" role. Otherwise allow exporting.
      const allowExporting = !(isSampleProject && isUserRole);
      console.log('Is User Role:', isUserRole, allowExporting);
      // Ensure Highcharts global exporting option follows our allowExporting flag
      useEffect(() => {
        try {
          Highcharts.setOptions({
            exporting: {
              enabled: allowExporting,
            },
          });
        } catch (e) {
          // Ignore if Highcharts isn't ready; this is defensive
          console.warn('Failed to set Highcharts exporting option', e);
        }
      }, [allowExporting]);
  

  const options1 = useMemo(() => {
    return {
      chart: {
        type: 'column',
        ...baseChartSpacing,
      },
      title: {
        text: 'Soil Properties',
        align: 'left' as const,
      },
      xAxis: {
        categories: soilPropertyDataCategories,
        title: {
          text: 'Depth Levels (cm)',
        },
      },
      yAxis: {
        title: {
          text: 'Percentage (%)',
        },
        labels: {
          formatter: function (this: Highcharts.AxisLabelsFormatterContextObject): string {
            return this.value + '%';
          },
        },
      },
      legend: {
        enabled: true,
        labelFormatter: function (this: Highcharts.Point): string {
          // Accessing the name property from the point
          const legendName = this.name?.replace(/_/g, ' ') ?? '';
          return legendName.charAt(0).toUpperCase() + legendName.slice(1);
        },
      },
      exporting: { enabled: allowExporting },
      tooltip: {
        formatter: function (this: Highcharts.TooltipFormatterContextObject): string {
          // Custom tooltip content using the added `tooltipValue`
          const depthLevel = this.point.category; // Category from xAxis
          const seriesName = this.series.name.charAt(0).toUpperCase() + this.series.name.slice(1); // Series name
          const percentage = this.y?.toFixed(2);
          return `<b>${seriesName}</b><br><b>Depth Level</b>: ${depthLevel}<br><b>Percentage:</b>${percentage}%`;
        },
      },
      plotOptions: {
        series: {
          grouping: true,
          borderWidth: 0,
        },
      },
      series,
      
    };
  }, [soilPropertyDataCategories, series]);

  const options2 = useMemo(() => {
    return {
      chart: {
        type: 'column',
        ...baseChartSpacing,
      },
      exporting: { enabled: allowExporting },
      title: {
        text: 'Soil Organic Carbon (SOC)',
        align: 'left' as const,
      },
      xAxis: {
        categories: soilDataCategories, // Use extracted depth levels
        title: {
          text: 'Depth Levels (cm)',
        },
      },
      yAxis: {
        title: {
          text: 'tc/ha', // Updated to match the unit from JSON
        },
      },
      legend: {
        enabled: false,
      },
      tooltip: {
        formatter: function (this: Highcharts.TooltipFormatterContextObject): string {
          return `
          <b>Depth:</b> ${this.x}<br/>
          <b>SOC:</b> ${(this.y as number).toFixed(2)} tc/ha
        `;
        },
      },
      series: series2, // Correctly assign the series
    };
  }, [soilDataCategories, series2]);

  const config = useMemo(() => {
    return {
      chart: {
        type: 'area',
        ...baseChartSpacing,
      },
      title: {
        text: 'Land Use Land Cover (in ha)',
        align: 'left' as const,
      },
      xAxis: {
        categories: categories,
        title: {
          text: 'Year',
        },
      },
      exporting: { enabled: allowExporting },
      yAxis: {
        title: {
          text: 'Hectares (ha)',
        },
        labels: {
          formatter: function (this: Highcharts.AxisLabelsFormatterContextObject): string {
            return `${this.value}`; // Convert value to string
          },
        },
      },
      tooltip: {
        shared: true,
        formatter: function (this: Highcharts.TooltipFormatterContextObject): string {
          return (
            `<b>${this.x}</b><br/>` +
            this.points
              ?.map(
                (point) =>
                  `<span style="color:${point.color}">\u25CF</span> ${point.series.name}: <b>${point.y?.toFixed()} ha</b>`,
              )
              .join('<br/>')
          );
        },
      },
      plotOptions: {
        area: {
          stacking: 'normal',
          lineColor: '#fff',
          lineWidth: 1,
          marker: {
            enabled: false,
          },
        },
      },
      series: seriesData,
    };
  }, [categories, seriesData]);

  const siteAgbLayers: CustomRasterLayer[] =
    site?.satellite_imagery
      ? (site.satellite_imagery as CustomRasterLayer[]).filter(
          (s: CustomRasterLayer) => s.type === 'AGB',
        )
      : [];

  const carbonSiteRasterLayers: CustomRasterLayer[] = [
    ...CARBON_GLOBAL_LAYERS,
    ...siteAgbLayers,
  ];

  if (!carbonAnalysisData) {
    return null;
  }
  let transformedData: { name: string; value1: number; value2: number }[] = [];
  if (
    filterTransitionData &&
    typeof filterTransitionData === 'object' &&
    Object.keys(filterTransitionData).length > 0 &&
    filterTransitionData[2000] &&
    filterTransitionData[2022]
  ) {
    transformedData = Object.keys(filterTransitionData[2000])?.map((key) => ({
      name: key,
      value1: !isNaN(Number(filterTransitionData[2000][key]))
        ? Number(filterTransitionData[2000][key].toFixed(2))
        : 0, // 2000 value
      value2: !isNaN(Number(filterTransitionData[2022][key]))
        ? Number(filterTransitionData[2022][key].toFixed(2))
        : 0, // 2022 value
    }));
  }

  const transformedSurfaceWaterData = Object.keys(filterWaterData)
    .filter((key) => key !== 'unit') // Exclude the unit key
    .map((key) => ({
      name: key,
      value1: Number(filterWaterData[key].toFixed(2)),
    }));

  const carbonAnalysisCard = [
    {
      label: 'Total Above Ground Biomass',
      value: roundToTwoDecimalPlaces(carbonAnalysisData?.agb) + ' ' + carbonAnalysisData?.agb_unit,
      xs: 4,
      sm: 2,
    },
    {
      label: 'Total Mean Above Ground Biomass',
      value: roundToTwoDecimalPlaces(carbonAnalysisData?.mean_agb) + ' ' + carbonAnalysisData?.mean_agb_unit,
      xs: 4,
      sm: 3,
    },
    {
      label: 'Total Cstock Above Ground Biomass',
      value: roundToTwoDecimalPlaces(carbonAnalysisData?.cstock_agb) + ' ' + carbonAnalysisData?.cstock_agb_unit,
      xs: 4,
      sm: 3,
    },
    {
      label: 'Total Mean Cstock Above Ground Biomass',
      value: roundToTwoDecimalPlaces(carbonAnalysisData?.mean_cstock_agb) + ' ' + carbonAnalysisData?.mean_cstock_agb_unit,
      xs: 4,
      sm: 2,
    },
    {
      label: 'Total Below Ground Biomass',
      value: roundToTwoDecimalPlaces(carbonAnalysisData?.bgb) + ' ' + carbonAnalysisData?.bgb_unit,
      xs: 4,
      sm: 2,
    },
    {
      label: 'Total Mean Below Ground Biomass',
      value: roundToTwoDecimalPlaces(carbonAnalysisData?.mean_bgb) + ' ' + carbonAnalysisData?.mean_bgb_unit,
      xs: 4,
      sm: 3,
    },
    {
      label: 'Total Cstock Below Ground Biomass',
      value: roundToTwoDecimalPlaces(carbonAnalysisData?.cstock_bgb) + ' ' + carbonAnalysisData?.cstock_bgb_unit,
      xs: 4,
      sm: 3,
    },
    {
      label: 'Total Mean Cstock Below Ground Biomass',
      value: roundToTwoDecimalPlaces(carbonAnalysisData?.mean_cstock_bgb) + ' ' + carbonAnalysisData?.mean_cstock_bgb_unit,
      xs: 4,
      sm: 2,
    },
  ];

  // const carbonProjectAnalysis: CarbonSiteAnalysis[] | undefined = projectDetails?.carbon_analysis;
  return (
    <Box sx={{ mt: 4 }}>
      {site && (
        <Box className="bio-map" sx={{ mt: 2 }} height={600}>
          <SiteMapWithLayer
            sites={[site]}
            fitToBoundary={true}
            rasterLayer={CARBON_GLOBAL_LAYERS}
          />
        </Box>
      )}
      <Box sx={{ mb: 3 }}>
        <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <SiteCard
              title={'Area Covered'}
              amount={`${roundToTwoDecimalPlaces(site?.area)} ${projectDetails?.area_unit}`}
              icon={SiteArea}
            />
          </Grid>
          {carbonAnalysisData?.plantation_area && <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <SiteCard
              title={'Total Plantation Area'}
              amount={`${roundToTwoDecimalPlaces(carbonAnalysisData?.plantation_area)} ${carbonAnalysisData?.plantation_area_unit}`}
              icon={SitePlantation}
            />
          </Grid>}
          {carbonAnalysisData?.planted_trees && <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <SiteCard
              title={'Total Planted Trees'}
              amount={carbonAnalysisData?.planted_trees || 0}
              icon={SiteTree}
            />
          </Grid>}
        </Grid>
      </Box>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12 }}>
          <Card
            sx={{
              boxShadow: '0px 0px 5px 0px rgba(0, 0, 0, 0.1019607843) !important',
              borderRadius: '8px',
              backgroundColor: '#fff',
            }}
          >
            {categories.length > 0 && seriesData.length > 0 && (
              <HighchartsReact highcharts={Highcharts} options={config} />
            )}
          </Card>
        </Grid>
        {transformedData.length ? (
          <Grid size={{ xs: 6 }}>
            <ChartComponent
              chartType={'pie'}
              chartTitle={'Land Cover Area Distribution - 2000 (in ha)'}
              data={transformedData.map((item) => ({ name: item.name, value1: item.value1 }))}
              seriesValue1="Area"
              export={allowExporting}
              
            />
          </Grid>
        ) : null}
        {transformedData.length ? (
          <Grid size={{ xs: 6 }}>
            <ChartComponent
              chartType={'pie'}
              chartTitle={'Land Cover Area Distribution - 2022 (in ha)'}
              data={transformedData.map((item) => ({ name: item.name, value1: item.value2 }))}
              seriesValue1="Area"
              export={allowExporting}
            />
          </Grid>
        ) : null}
        {transformedSurfaceWaterData.length ? <Grid size={{ xs: 12 }}>
          <ChartComponent
            chartType={'pie'}
            chartTitle={'Surface Water Transition (1984-2021) (in ha)'}
            data={transformedSurfaceWaterData}
            seriesValue1="Area"
            export={allowExporting}
          />
        </Grid> : null}
        <Grid size={{ xs: 12 }}>
          <Card
            sx={{
              boxShadow: '0px 0px 5px 0px rgba(0, 0, 0, 0.1019607843) !important',
              borderRadius: '8px',
              backgroundColor: '#fff',
            }}
          >
            {/* <Typography
              component="div"
              sx={{
                textAlign: 'left',
                fontSize: '16px',
                fontWeight: 600,
                color: '#151E15',
                p: '16px',
              }}
            >
              {'Soil Properties'}
            </Typography> */}
            {/* <Box className="divider" sx={{ m: 2 }} /> */}
            <HighchartsReact highcharts={Highcharts} options={options1} />
          </Card>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Card
            sx={{
              boxShadow: '0px 0px 5px 0px rgba(0, 0, 0, 0.1019607843) !important',
              borderRadius: '8px',
              backgroundColor: '#fff',
            }}
          >
            {/* <Typography
              component="div"
              sx={{
                textAlign: 'left',
                fontSize: '16px',
                fontWeight: 600,
                color: '#151E15',
                p: '16px',
              }}
            >
              {'Soil Organic Carbon (SOC)'}
            </Typography> */}
            {/* <Box className="divider" sx={{ m: 2 }} /> */}
            <HighchartsReact highcharts={Highcharts} options={options2} />
          </Card>
        </Grid>
      </Grid>

      <Box>
        <Typography sx={{ fontSize: '24px', fontWeight: 600, color: '#151E15' }}>
          {'Biomass & Cstock'}
        </Typography>
      </Box>
      {site && (
        <Box className="bio-map" sx={{ mt: 3 }} height={600}>
          <SiteMapWithLayer
            sites={[site]}
            fitToBoundary={true}
            rasterLayer={carbonSiteRasterLayers}
            // rasterLayer={site.satellite_imagery ? (site.satellite_imagery as CustomRasterLayer[])?.filter((s: CustomRasterLayer) => s.type === 'AGB') : []}
          ></SiteMapWithLayer>
        </Box>
      )}
      <Box className="project-box">
        <Grid container>
          <CreditCard data={carbonAnalysisCard} />
        </Grid>
      </Box>
    </Box>
  );
};

export default CarbonAnalysisDetails;